





















































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import AuditKepatuhanPpiService from "@/services/AuditKepatuhanPpiService";
import { SEKSI_ORDER, METODE_LEGENDA } from "@/constants/seksiAuditKepatuhanPpi";

type Hasil = "ya" | "tidak" | "na" | null;

interface Row {
  _k: string;
  id?: number;
  seksi: string;
  item_kode: string | null;
  item_teks: string;
  metode: string | null;
  hasil: Hasil;
  keterangan: string;
  is_custom: boolean;
}

@Component
export default class AuditKepatuhanPpiItemLogger extends Vue {
  @Prop({ required: true }) sessionId!: number;

  session: any = null;
  sessionNomor = "";
  seksiMeta: Record<string, any> = {};
  rows: Row[] = [];
  metodeLegenda = METODE_LEGENDA;

  saving = false;
  submitting = false;
  submitDialog = false;
  submitForm: any = { password: "" };
  dirty = false;
  keyCounter = 0;

  async mounted() {
    await this.loadCatalog();
    await this.loadSession();
    this.initRows();
  }

  async loadCatalog() {
    const res = await AuditKepatuhanPpiService.getCatalog();
    this.seksiMeta = res.data.data.seksi || {};
  }

  async loadSession() {
    const res = await AuditKepatuhanPpiService.find(this.sessionId);
    this.session = res.data.data;
    this.sessionNomor = this.session.nomor_formulir;
  }

  nextKey(): string {
    this.keyCounter += 1;
    return "row-" + this.keyCounter;
  }

  initRows() {
    this.rows = (this.session.items || []).map((it: any) => ({
      _k: this.nextKey(),
      id: it.id,
      seksi: it.seksi,
      item_kode: it.item_kode,
      item_teks: it.item_teks,
      metode: it.metode,
      hasil: (it.hasil as Hasil) ?? null,
      keterangan: it.keterangan || "",
      is_custom: !!it.is_custom
    }));
  }

  get sections() {
    return SEKSI_ORDER.filter(k => this.seksiMeta[k]).map(key => {
      const meta = this.seksiMeta[key];
      const rows = this.rows.filter(r => r.seksi === key);
      return { key, meta, rows };
    }).filter(s => s.rows.length > 0 || (s.meta && s.meta.custom));
  }

  addCustom(seksi: string) {
    this.rows.push({
      _k: this.nextKey(),
      seksi,
      item_kode: null,
      item_teks: "",
      metode: this.seksiMeta[seksi] ? this.seksiMeta[seksi].metode : null,
      hasil: null,
      keterangan: "",
      is_custom: true
    });
    this.markDirty();
  }

  removeCustom(seksi: string, indexInSection: number) {
    const sectionRows = this.rows.filter(r => r.seksi === seksi);
    const target = sectionRows[indexInSection];
    if (!target) return;
    const idx = this.rows.indexOf(target);
    if (idx >= 0) this.rows.splice(idx, 1);
    this.markDirty();
  }

  setHasil(row: Row, v: "ya" | "tidak") {
    row.hasil = row.hasil === v ? null : v;
    this.markDirty();
  }

  clearAllHasil() {
    this.rows.forEach(r => (r.hasil = null));
    this.markDirty();
  }

  markDirty() {
    this.dirty = true;
  }

  formatDate(s: string) {
    if (!s) return "-";
    return new Date(s).toLocaleDateString("id-ID");
  }

  countHasil(rows: Row[], v: "ya" | "tidak"): number {
    return rows.filter(r => r.hasil === v).length;
  }

  subRate(section: any): number | null {
    const ya = this.countHasil(section.rows, "ya");
    const tidak = this.countHasil(section.rows, "tidak");
    const denom = ya + tidak;
    return denom > 0 ? Math.round((ya / denom) * 10000) / 100 : null;
  }

  subColor(section: any): string {
    const r = this.subRate(section);
    if (r === null) return "grey";
    if (r >= 85) return "success";
    if (r >= 76) return "warning";
    return "error";
  }

  get totalItems(): number {
    return this.rows.length;
  }

  get filledCount(): number {
    return this.rows.filter(r => r.hasil === "ya" || r.hasil === "tidak").length;
  }

  get totalYa(): number {
    return this.countHasil(this.rows, "ya");
  }

  get totalTidak(): number {
    return this.countHasil(this.rows, "tidak");
  }

  get overallRate(): number | null {
    const denom = this.totalYa + this.totalTidak;
    return denom > 0 ? Math.round((this.totalYa / denom) * 10000) / 100 : null;
  }

  get overallLabel(): string {
    const r = this.overallRate;
    if (r === null) return "Belum dinilai";
    if (r >= 85) return "BAIK";
    if (r >= 76) return "CUKUP";
    return "KURANG";
  }

  get overallColor(): string {
    const r = this.overallRate;
    if (r === null) return "grey";
    if (r >= 85) return "success";
    if (r >= 76) return "warning";
    return "error";
  }

  get canSubmit(): boolean {
    return this.filledCount > 0 && !this.dirty;
  }

  buildPayload() {
    return this.rows
      .filter(r => !(r.is_custom && !(r.item_teks || "").trim()))
      .map(r => ({
        id: r.id,
        seksi: r.seksi,
        item_teks: r.item_teks,
        hasil: r.hasil,
        keterangan: r.keterangan,
        is_custom: r.is_custom
      }));
  }

  async saveAll() {
    this.saving = true;
    try {
      await AuditKepatuhanPpiService.syncItems(this.sessionId, this.buildPayload());
      this.dirty = false;
      await this.loadSession();
      this.initRows();
    } finally {
      this.saving = false;
    }
  }

  openSubmitDialog() {
    this.submitForm = { password: "" };
    this.submitDialog = true;
  }

  async confirmSubmit() {
    this.submitting = true;
    try {
      await AuditKepatuhanPpiService.submitAuditor(this.sessionId, this.submitForm);
      this.submitDialog = false;
      this.$emit("submitted");
    } finally {
      this.submitting = false;
    }
  }
}
